import { MyWorkspaces } from "@minga/protocol"
import { type QueryClient, queryOptions } from "@tanstack/react-query"
import { ApiError, apiJson, isAbortOrTimeout, RequestTimeout, timeoutText } from "./api"
import { authClient } from "./auth-client"
import { fetchFrom, GUEST_PAGE, takeHtmlSession, takeHtmlWorkspaces } from "./early-boot"
import { markBoot } from "./telemetry"

/**
 * How long the app waits for the session and the workspace list before it shows "Couldn't reach Minga" (decision
 * 0034). Every signed-in page waits on both, so a hanging server must never mean a page that never appears.
 */
export const SESSION_TIMEOUT_MS = 10_000

/** Retry once for network or server trouble; never a timeout (it would double the wait) or a 4xx answer. */
const retryOnce = (failureCount: number, error: unknown): boolean =>
  failureCount < 1 &&
  !(error instanceof RequestTimeout) &&
  !(error instanceof ApiError && error.status >= 400 && error.status < 500)

export const sessionQuery = queryOptions({
  queryKey: ["auth", "session"],
  queryFn: async () => {
    try {
      // decision 0160: the first read takes the request index.html sent before the app's code loaded
      const early = takeHtmlSession()
      const fresh = early && Date.now() - early.at <= SESSION_TIMEOUT_MS ? early : null
      // better-fetch does not catch fetch() itself, so an abort throws instead of returning `{ error }`.
      const { data, error } = await authClient.getSession({
        fetchOptions: {
          signal: AbortSignal.timeout(SESSION_TIMEOUT_MS),
          ...(fresh ? { customFetchImpl: fetchFrom(fresh.response) } : {}),
        },
      })
      if (error) throw new ApiError(error.status, error.message ?? "Could not load session")
      markBoot("session")
      return data
    } catch (e) {
      if (isAbortOrTimeout(e)) throw new RequestTimeout(timeoutText(SESSION_TIMEOUT_MS))
      throw e
    }
  },
  retry: retryOnce,
  staleTime: 30_000,
})

/**
 * H11 `GET /api/me/workspaces`: the caller's workspaces with their role, and the session's "last used" one. The key
 * is `["me","workspaces"]` on purpose: `["workspaces"]` would prefix-match every `["workspaces", ws, …]` query.
 */
export const workspacesQuery = queryOptions({
  queryKey: ["me", "workspaces"],
  queryFn: async () => {
    const list = await (takeEarlyWorkspaces() ?? fetchWorkspaces())
    markBoot("workspaces")
    return list
  },
  staleTime: 60_000,
})

const fetchWorkspaces = (fetchImpl?: typeof fetch) =>
  apiJson("/api/me/workspaces", MyWorkspaces, {
    timeoutMs: SESSION_TIMEOUT_MS,
    ...(fetchImpl ? { fetchImpl } : {}),
  })

/**
 * Boot in parallel (performance plan item 2): the workspace list is requested at startup together with the session
 * instead of after it, and the first `workspacesQuery` fetch takes that answer. Only that first fetch, only within
 * `SESSION_TIMEOUT_MS` of startup, and never for a signed-out person: a 4xx answer (signed out at startup, signed in
 * since) is asked again, so it can't send anyone back to sign-in.
 */
let early: { readonly promise: Promise<MyWorkspaces>; readonly at: number } | null = null

/** Start the session and the workspace list together, before the router runs. Never throws. */
export const startBootRequests = (queryClient: QueryClient, pathname: string): void => {
  void queryClient.prefetchQuery(sessionQuery).then(() => {
    // Signed out: the early list (a 401) is never used.
    if (queryClient.getQueryData(sessionQuery.queryKey) === null) early = null
  })
  if (GUEST_PAGE.test(pathname)) return
  // decision 0160: the list index.html already asked for before the app's code loaded; else ask now
  const html = takeHtmlWorkspaces()
  const promise = fetchWorkspaces(html ? fetchFrom(html.response) : undefined)
  promise.catch(() => {}) // used (or not) by the first workspacesQuery fetch
  early = { promise, at: html?.at ?? Date.now() }
}

/** The startup request for the workspace list, once; null when there is none or it is too old to trust. */
export const takeEarlyWorkspaces = (): Promise<MyWorkspaces> | null => {
  const taken = early
  early = null
  if (!taken || Date.now() - taken.at > SESSION_TIMEOUT_MS) return null
  return taken.promise.catch((error: unknown) => {
    if (error instanceof ApiError && error.status >= 400 && error.status < 500) return fetchWorkspaces()
    throw error
  })
}

/** Forget the startup request (sign-out, a 401): the next list is read fresh. */
export const dropEarlyWorkspaces = (): void => {
  early = null
}

/** Re-reads the session from the server (after sign-in/up/out), bypassing the cache. */
export const refreshSession = (queryClient: QueryClient) =>
  queryClient.fetchQuery({ ...sessionQuery, staleTime: 0 })
